import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { GENERIC_FILTER_PARAMS_QUERY } from 'features/genericFilters/queries';
import { UPDATE_CALCULATION_INFO } from '../../../mutations';
import { CONVENTION_GENERAL_DATA_UPDATED } from '../../../subscriptions';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useToast } from 'hooks/toasts/useToast';
import { useTranslation } from 'react-i18next';
import { get, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { insuranceFormulaContextCode } from '../../../constants';
import InsuranceConventionPercentCalculationComponent from './InsuranceConventionPercentCalculationComponent';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';

const InsuranceConventionPercentCalculationContainer = ({ insuranceConventionData }) => {
  const [params, setParams] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens();
  const addToast = useToast();
  const { t } = useTranslation();
  const isNewConvention = useMemo(() => !insuranceConventionData?.productId, [insuranceConventionData?.productId]);
  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useGlobalIsDirty(isDirty);

  useQuery(GENERIC_FILTER_PARAMS_QUERY, {
    variables: {
      context: insuranceFormulaContextCode
    },
    onCompleted: data => {
      setParams(data?.genericFilterParams || []);
    }
  });

  useEffect(() => {
    resetLens(insuranceConventionData);
  }, [insuranceConventionData, resetLens]);

  const [updateCalculationInfo] = useMutation(UPDATE_CALCULATION_INFO, {
    onError: () => {
      setSaving(false);
    }
  });

  const handleSave = useCallback(() => {
    const insuranceConvention = get(lens);
    setSaving(true);
    // eslint-disable-next-line no-unused-vars
    updateCalculationInfo({
      variables: {
        input: {
          productId: insuranceConvention.productId,
          insuranceValueFormula: insuranceConvention.insuranceValueFormula,
          baseInsurancePercentFormula: insuranceConvention.baseInsurancePercentFormula,
          minInsuranceAmount: insuranceConvention.minInsuranceAmount
        }
      }
    });
  }, [lens, updateCalculationInfo]);

  useSubscription(CONVENTION_GENERAL_DATA_UPDATED, {
    onData: ({
      data: {
        data: { insuranceConventionGeneralDataUpdated }
      },
      client
    }) => {
      if (!insuranceConventionGeneralDataUpdated?.convention?.calculationInfoChanged) return;
      resetLens(insuranceConventionGeneralDataUpdated.convention);
      setSaving(false);
      client.cache.modify({
        fields: {
          insuranceConventionsList(existing) {
            return [existing, insuranceConventionGeneralDataUpdated.convention];
          }
        }
      });
      addToast(t('InsuranceConventions.Details.PercentCalculation.PercentCalculationUpdated'), 'success');
    }
  });

  return (
    <InsuranceConventionPercentCalculationComponent
      params={params}
      lens={lens}
      onSave={handleSave}
      saving={saving}
      isDirty={isDirty}
      isNewConvention={isNewConvention}
    />
  );
};

InsuranceConventionPercentCalculationContainer.propTypes = {
  insuranceConventionData: PropTypes.object
};

export default InsuranceConventionPercentCalculationContainer;
